/**
 * Shared layout math for the editor textarea and synced overlay (line highlight, scroll-to-line).
 * One source of truth avoids subpixel drift between `textarea` and highlight `<pre>` on huge buffers.
 *
 * For the measured grid (`scrollHeight`–driven row pitch), we avoid computing `h = inner / N` and
 * then multiplying back (which loses precision). Ratios like `scrollTop * N / inner` use micro-pixel
 * BigInt intermediates when `scrollTop * N` would exceed `Number.MAX_SAFE_INTEGER`.
 */

const FALLBACK_LINE_HEIGHT_FACTOR = 1.45;

function toMicroPx(x: number): bigint {
  if (!Number.isFinite(x) || x <= 0) return 0n;
  return BigInt(Math.round(x * 1_000_000));
}

function microPxToNumber(m: bigint): number {
  return Number(m) / 1_000_000;
}

/** `floor(scrollTopPx * lineCount / innerPx)`; `lineCount` is an exact integer. */
export function floorScrollTimesLinesOverInner(scrollTopPx: number, lineCount: number, innerPx: number): number {
  if (innerPx <= 0 || lineCount <= 0) return 0;
  const st = Math.max(0, scrollTopPx);
  const n = Math.floor(lineCount);
  const inner = Math.max(0, innerPx);
  const prod = st * n;
  if (Number.isFinite(prod) && Math.abs(prod) <= Number.MAX_SAFE_INTEGER * 0.999) {
    return Math.floor(prod / inner + 1e-15);
  }
  const A = toMicroPx(st);
  const D = toMicroPx(inner);
  const B = BigInt(Math.max(0, n));
  return Number((A * B) / D);
}

/** `ceil(clientHeightPx * lineCount / innerPx)` for nonnegative values. */
export function ceilClientTimesLinesOverInner(clientHeightPx: number, lineCount: number, innerPx: number): number {
  if (innerPx <= 0 || lineCount <= 0) return 0;
  const ch = Math.max(0, clientHeightPx);
  const n = Math.floor(lineCount);
  const inner = Math.max(0, innerPx);
  const prod = ch * n;
  if (Number.isFinite(prod) && Math.abs(prod) <= Number.MAX_SAFE_INTEGER * 0.999) {
    return Math.ceil(prod / inner - 1e-15);
  }
  const C = toMicroPx(ch);
  const D = toMicroPx(inner);
  const B = BigInt(Math.max(0, n));
  return Number((C * B + D - 1n) / D);
}

/**
 * scrollTop so logical line `lineIndex0` sits near `anchorFraction` of viewport height (measured grid).
 * Uses `(lineIndex0 * inner - anchor * clientHeight * N) / N` in micro-pixel integer arithmetic.
 */
export function scrollTopToShowLogicalLineMeasured(opts: {
  lineIndex0: number;
  innerContentPx: number;
  lineCount: number;
  clientHeight: number;
  anchorFraction?: number;
}): number {
  const a = opts.anchorFraction ?? 0.35;
  const anchorPx = a * opts.clientHeight;
  const li = Math.max(0, Math.floor(opts.lineIndex0));
  const n = BigInt(Math.max(1, Math.floor(opts.lineCount)));
  const D = toMicroPx(opts.innerContentPx);
  const anchorM = toMicroPx(anchorPx);
  const num = BigInt(li) * D - anchorM * n;
  const stM = num / n;
  const st = microPxToNumber(stM < 0n ? 0n : stM);
  return Number.isFinite(st) ? Math.max(0, st) : 0;
}

/** Active-line band top (viewport px) under the measured uniform grid. */
export function activeLineOverlayTopMeasured(opts: {
  lineIndex0: number;
  innerContentPx: number;
  lineCount: number;
  paddingTopPx: number;
  editorScrollTop: number;
}): number {
  const li = Math.max(0, Math.floor(opts.lineIndex0));
  const n = BigInt(Math.max(1, Math.floor(opts.lineCount)));
  const D = toMicroPx(opts.innerContentPx);
  const padM = toMicroPx(opts.paddingTopPx);
  const scrM = toMicroPx(opts.editorScrollTop);
  const num = padM * n + BigInt(li) * D - scrM * n;
  return microPxToNumber(num / n);
}

/** Line-number gutter `padding-top` for start line `startLine1` (1-based), measured grid. */
export function lineNumberGutterPaddingTopMeasured(opts: {
  startLine1: number;
  innerContentPx: number;
  lineCount: number;
  paddingTopPx: number;
  editorScrollTop: number;
}): number {
  const s = Math.max(1, Math.floor(opts.startLine1));
  const li = s - 1;
  const n = BigInt(Math.max(1, Math.floor(opts.lineCount)));
  const D = toMicroPx(opts.innerContentPx);
  const padM = toMicroPx(opts.paddingTopPx);
  const scrM = toMicroPx(opts.editorScrollTop);
  const num = padM * n + BigInt(li) * D - scrM * n;
  return microPxToNumber(num / n);
}

/** True iff `resolvedLineHeightPx * lineCount > innerContentPx` (cross-multiply in micro space, no FP h). */
export function resolvedRowsDominateMeasured(
  resolvedLineHeightPx: number,
  lineCount: number,
  innerContentPx: number,
): boolean {
  const n = Math.max(1, Math.floor(lineCount));
  const inner = Math.max(0, innerContentPx);
  const r = Math.max(0, resolvedLineHeightPx);
  return toMicroPx(r) * BigInt(n) > toMicroPx(inner);
}

/** Row model: either one row pitch from `scrollHeight` / line count, or fixed CSS `line-height`. */
export type EditorLineUniformModel =
  | { mode: "measuredRatio"; innerContentPx: number; lineCount: number }
  | { mode: "fixedRow"; rowPx: number; lineCount: number };

export function buildEditorLineUniformModel(opts: {
  scrollHeight: number;
  paddingTopPx: number;
  paddingBottomPx: number;
  logicalLineCount: number;
  resolvedLineHeightPx: number;
  clientHeight?: number;
}): EditorLineUniformModel {
  const lineCount = Math.max(1, Math.floor(opts.logicalLineCount));
  const inner = opts.scrollHeight - opts.paddingTopPx - opts.paddingBottomPx;
  const innerContentPx = Number.isFinite(inner) && inner > 0 ? inner : 0;
  const res = opts.resolvedLineHeightPx > 0 ? opts.resolvedLineHeightPx : 14;
  if (innerContentPx <= 0) {
    return { mode: "fixedRow", rowPx: res, lineCount };
  }
  const ch = opts.clientHeight;
  if (typeof ch === "number" && Number.isFinite(ch) && opts.scrollHeight <= ch) {
    return { mode: "fixedRow", rowPx: res, lineCount };
  }
  if (resolvedRowsDominateMeasured(res, lineCount, innerContentPx)) {
    return { mode: "fixedRow", rowPx: res, lineCount };
  }
  return { mode: "measuredRatio", innerContentPx, lineCount };
}

export function rowHeightPxFromUniformModel(m: EditorLineUniformModel): number {
  if (m.mode === "fixedRow") {
    return m.rowPx > 0 ? m.rowPx : 14;
  }
  const n = BigInt(Math.max(1, m.lineCount));
  const D = toMicroPx(m.innerContentPx);
  return microPxToNumber(D / n);
}

export function firstVisibleLineOneBasedFromModel(scrollTop: number, m: EditorLineUniformModel): number {
  const st = Math.max(0, scrollTop);
  if (m.mode === "fixedRow") {
    const row = m.rowPx > 0 ? m.rowPx : 14;
    if (row <= 0) return 1;
    const A = toMicroPx(st);
    const R = toMicroPx(row);
    const i0 = Number(A / R);
    return Math.max(1, Math.min(m.lineCount, i0 + 1));
  }
  const n = m.lineCount;
  const raw = floorScrollTimesLinesOverInner(st, n, m.innerContentPx);
  const i0 = Math.min(Math.max(0, n - 1), Math.max(0, raw));
  return i0 + 1;
}

export function scrollTopToShowLogicalLineFromModel(opts: {
  lineIndex0: number;
  clientHeight: number;
  anchorFraction?: number;
  model: EditorLineUniformModel;
}): number {
  const a = opts.anchorFraction ?? 0.35;
  if (opts.model.mode === "fixedRow") {
    const row = opts.model.rowPx > 0 ? opts.model.rowPx : 14;
    return scrollTopToShowLogicalLine({
      lineIndex0: opts.lineIndex0,
      lineHeightPx: row,
      clientHeight: opts.clientHeight,
      anchorFraction: a,
    });
  }
  return scrollTopToShowLogicalLineMeasured({
    lineIndex0: opts.lineIndex0,
    innerContentPx: opts.model.innerContentPx,
    lineCount: opts.model.lineCount,
    clientHeight: opts.clientHeight,
    anchorFraction: a,
  });
}

export function activeLineOverlayTopFromModel(opts: {
  lineIndex0: number;
  paddingTopPx: number;
  editorScrollTop: number;
  model: EditorLineUniformModel;
}): number {
  if (opts.model.mode === "fixedRow") {
    const row = opts.model.rowPx > 0 ? opts.model.rowPx : 14;
    return activeLineOverlayTopPx({
      lineIndex0: opts.lineIndex0,
      lineHeightPx: row,
      paddingTopPx: opts.paddingTopPx,
      editorScrollTop: opts.editorScrollTop,
    });
  }
  return activeLineOverlayTopMeasured({
    lineIndex0: opts.lineIndex0,
    innerContentPx: opts.model.innerContentPx,
    lineCount: opts.model.lineCount,
    paddingTopPx: opts.paddingTopPx,
    editorScrollTop: opts.editorScrollTop,
  });
}

export function lineNumberGutterPaddingTopFromModel(opts: {
  startLine1: number;
  paddingTopPx: number;
  editorScrollTop: number;
  model: EditorLineUniformModel;
}): number {
  if (opts.model.mode === "fixedRow") {
    const row = opts.model.rowPx > 0 ? opts.model.rowPx : 14;
    const s = Math.max(1, Math.floor(opts.startLine1));
    const li = s - 1;
    const padM = toMicroPx(opts.paddingTopPx);
    const scrM = toMicroPx(opts.editorScrollTop);
    const rowM = toMicroPx(row);
    return microPxToNumber(padM + BigInt(li) * rowM - scrM);
  }
  return lineNumberGutterPaddingTopMeasured({
    startLine1: opts.startLine1,
    innerContentPx: opts.model.innerContentPx,
    lineCount: opts.model.lineCount,
    paddingTopPx: opts.paddingTopPx,
    editorScrollTop: opts.editorScrollTop,
  });
}

/** How many logical line slots to paint in the gutter (ceil of viewport / row pitch), plus caller slack. */
export function visibleLogicalLineSlotsCeil(clientHeight: number, m: EditorLineUniformModel): number {
  const ch = Math.max(0, clientHeight);
  if (m.mode === "fixedRow") {
    const row = m.rowPx > 0 ? m.rowPx : 14;
    if (row <= 0) return 1;
    const C = toMicroPx(ch);
    const R = toMicroPx(row);
    return Math.max(1, Number((C + R - 1n) / R));
  }
  return Math.max(1, ceilClientTimesLinesOverInner(ch, m.lineCount, m.innerContentPx));
}

/** Resolved used line-height in px (same rules as former inline code in AppShell). */
export function resolvedLineHeightPx(cs: CSSStyleDeclaration): number {
  const raw = cs.lineHeight;
  if (typeof raw === "string" && raw.endsWith("px") && Number.isFinite(Number.parseFloat(raw))) {
    return Number.parseFloat(raw);
  }
  const fs = cs.fontSize;
  const fontPx =
    typeof fs === "string" && fs.endsWith("px") && Number.isFinite(Number.parseFloat(fs))
      ? Number.parseFloat(fs)
      : Number.parseFloat(fs || "14") || 14;
  return fontPx * FALLBACK_LINE_HEIGHT_FACTOR;
}

export function paddingTopPx(cs: CSSStyleDeclaration): number {
  const p = cs.paddingTop;
  if (typeof p !== "string") return 0;
  if (p.endsWith("px") && Number.isFinite(Number.parseFloat(p))) return Number.parseFloat(p);
  return Number.parseFloat(p) || 0;
}

export function paddingBottomPx(cs: CSSStyleDeclaration): number {
  const p = cs.paddingBottom;
  if (typeof p !== "string") return 0;
  if (p.endsWith("px") && Number.isFinite(Number.parseFloat(p))) return Number.parseFloat(p);
  return Number.parseFloat(p) || 0;
}

/**
 * Average content-row height from the textarea’s real `scrollHeight` vs logical line count.
 * Reduces gutter / highlight drift on very large `white-space: pre` buffers where computed
 * line-height differs slightly from the engine’s laid-out row height.
 */
export function avgContentLineHeightPx(opts: {
  scrollHeight: number;
  paddingTopPx: number;
  paddingBottomPx: number;
  logicalLineCount: number;
  resolvedLineHeightPx: number;
}): number {
  const m = buildEditorLineUniformModel({
    scrollHeight: opts.scrollHeight,
    paddingTopPx: opts.paddingTopPx,
    paddingBottomPx: opts.paddingBottomPx,
    logicalLineCount: opts.logicalLineCount,
    resolvedLineHeightPx: opts.resolvedLineHeightPx,
  });
  const h = rowHeightPxFromUniformModel(m);
  return Number.isFinite(h) && h > 0 ? h : opts.resolvedLineHeightPx;
}

/** scrollTop so logical line `lineIndex0` sits near `anchorFraction` of viewport height from the top. */
export function scrollTopToShowLogicalLine(opts: {
  lineIndex0: number;
  lineHeightPx: number;
  clientHeight: number;
  anchorFraction?: number;
}): number {
  const a = opts.anchorFraction ?? 0.35;
  const anchorPx = opts.clientHeight * a;
  const li = Math.max(0, Math.floor(opts.lineIndex0));
  const prod = li * opts.lineHeightPx;
  if (Number.isFinite(prod) && Math.abs(prod) <= Number.MAX_SAFE_INTEGER * 0.999) {
    return Math.max(0, li * opts.lineHeightPx - anchorPx);
  }
  const liB = BigInt(li);
  const rowM = toMicroPx(opts.lineHeightPx);
  const anchorM = toMicroPx(anchorPx);
  const stM = liB * rowM - anchorM;
  return Math.max(0, microPxToNumber(stM < 0n ? 0n : stM));
}

/** Top of active-line band inside the editor host (viewport coords), matching textarea content rows. */
export function activeLineOverlayTopPx(opts: {
  lineIndex0: number;
  lineHeightPx: number;
  paddingTopPx: number;
  editorScrollTop: number;
}): number {
  const li = Math.max(0, Math.floor(opts.lineIndex0));
  const prod = li * opts.lineHeightPx;
  if (Number.isFinite(prod) && Math.abs(prod) <= Number.MAX_SAFE_INTEGER * 0.999) {
    return opts.paddingTopPx + li * opts.lineHeightPx - opts.editorScrollTop;
  }
  const padM = toMicroPx(opts.paddingTopPx);
  const scrM = toMicroPx(opts.editorScrollTop);
  const liB = BigInt(li);
  const rowM = toMicroPx(opts.lineHeightPx);
  return microPxToNumber(padM + liB * rowM - scrM);
}

/** 1-based index of the first logical line whose top is at or above the scroll position. */
export function firstVisibleLineOneBased(scrollTop: number, lineHeightPx: number): number {
  if (lineHeightPx <= 0) return 1;
  const st = Math.max(0, scrollTop);
  const A = toMicroPx(st);
  const R = toMicroPx(lineHeightPx);
  const i0 = Number(A / R);
  return Math.max(1, i0 + 1);
}

/**
 * Clamp `scrollTop` to values the textarea can actually use for layout math.
 * Defensive: some engines briefly report out-of-range values during elastic scroll or layout.
 */
export function clampEditorScrollTop(scrollTop: number, scrollHeight: number, clientHeight: number): number {
  const st = Number.isFinite(scrollTop) ? scrollTop : 0;
  if (!Number.isFinite(scrollHeight) || !Number.isFinite(clientHeight)) {
    return Math.max(0, st);
  }
  const sh = Math.max(0, scrollHeight);
  const ch = Math.max(0, clientHeight);
  const max = ch <= 0 ? 0 : Math.max(0, sh - ch);
  return Math.min(Math.max(0, st), max);
}
